import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { AnimatePresence, motion } from 'framer-motion';
import { AgentThread, GetAgentThreadsResponse } from '@dreamai/types';
import api from '../../../api';
import { useThreadReads } from '../../../providers/ThreadReadsProvider';
import NewThreadModal from './NewThreadModal';
import { ReactComponent as ProjectsFolderIcon } from '../../../icons/projects-folder.svg';
import { ReactComponent as PlusIcon } from '../../../icons/plus.svg';
import { ReactComponent as ArrowLeftIcon } from '../../../icons/arrow-left.svg';
import { getProjectTagIconUrl } from '../../../utils/projectTagIcon';
import './projects-drawer.scss';

interface Props {
  open: boolean;
  agentId: string | undefined;
  agentName?: string;
  agentImageUrl?: string;
  agentType?: string;
  onClose: () => void;
  /** Element that toggles the drawer; click-outside detection ignores it
   *  so toggling doesn't immediately close-then-reopen. */
  toggleRef?: React.RefObject<HTMLElement | null>;
}

// ── Component ─────────────────────────────────────────────────────────────

/**
 * Sliding drawer that appears next to the ExpansiveSideNavigation and
 * lists the user's projects ordered by created_at desc. Mirrors the
 * RecentsDrawer layout but uses a project-specific card style with
 * 72×72 icon thumbnails.
 */
const ProjectsDrawer: React.FC<Props> = ({
  open,
  agentId,
  agentName,
  agentImageUrl,
  agentType,
  onClose,
  toggleRef,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const { getAllUnreadThreads } = useThreadReads();
  const [projects, setProjects] = useState<AgentThread[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  // Set of thread IDs the user hasn't read yet. Recomputed on every render so
  // the UNREAD pill reacts immediately when ThreadReadsProvider receives a
  // Pusher activity event — no need to refetch the project list.
  const unreadThreadIds = new Set(
    getAllUnreadThreads().map((t) => t.thread_id),
  );

  const drawerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);

  const fetchProjects = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

      const threadsRes = await api.get<GetAgentThreadsResponse>(
        '/agents/threads',
        {
          params: {
            agent_id: agentId,
            kind: 'Project',
            excludePinned: 'true',
            excludeShared: 'true',
            excludeSubThreads: 'true',
          },
          ...authHeaders,
        },
      );

      const threads = threadsRes.data.success ? threadsRes.data.threads : [];

      const projectList: AgentThread[] = threads
        .filter((t) => !t.is_archived_thread)
        .sort(
          (a, b) =>
            new Date(b.last_updated_at).getTime() -
            new Date(a.last_updated_at).getTime(),
        );

      setProjects(projectList);
      setHasFetched(true);
    } catch (err) {
      console.error('Failed to load projects:', err);
      setError('Could not load projects.');
    } finally {
      setLoading(false);
    }
  }, [agentId, getAccessTokenSilently]);

  // Fetch (or re-fetch) every time the drawer opens.
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      fetchProjects();
    }
    prevOpenRef.current = open;
  }, [open, fetchProjects]);

  // Close on click outside (ignoring the toggle button).
  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      const target = e.target as Node;
      if (drawerRef.current && drawerRef.current.contains(target)) return;
      if (toggleRef?.current && toggleRef.current.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open, onClose, toggleRef]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open, onClose]);

  const handleSelect = (threadId: string) => {
    window.location.assign(
      `/conversations?thread_id=${encodeURIComponent(threadId)}`,
    );
  };

  return (
    <>
      {agentId && (
        <NewThreadModal
          isOpen={showNewProjectModal}
          onClose={() => setShowNewProjectModal(false)}
          agentId={agentId}
          agentName={agentName}
          agentImageUrl={agentImageUrl}
          agentType={agentType}
          creationType='project'
          onThreadCreated={() => {
            fetchProjects();
          }}
        />
      )}
      {createPortal(
        <AnimatePresence>
          {open && (
            <React.Fragment key='projects-drawer-fragment'>
              <motion.div
                key='projects-drawer-shroud'
                className='projects-drawer-shroud'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                onClick={onClose}
              />
              <motion.aside
                ref={drawerRef}
                key='projects-drawer'
                className='projects-drawer'
                initial={{ x: -16, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -16, opacity: 0 }}
                transition={{
                  type: 'spring',
                  stiffness: 420,
                  damping: 34,
                  mass: 0.6,
                }}
              >
                <div className='projects-drawer-header'>
                  <button
                    type='button'
                    className='drawer-mobile-close'
                    onClick={onClose}
                    aria-label='Close'
                  >
                    <ArrowLeftIcon />
                  </button>
                  <div className='projects-drawer-header-top'>
                    <ProjectsFolderIcon className='projects-drawer-header-icon' />
                    {agentId && (
                      <button
                        type='button'
                        className='projects-drawer-new-btn'
                        onClick={() => setShowNewProjectModal(true)}
                      >
                        <PlusIcon className='projects-drawer-new-btn-icon' />
                        New
                      </button>
                    )}
                  </div>
                  <h2 className='projects-drawer-title'>Projects</h2>
                </div>

                <div ref={scrollRef} className='projects-drawer-scroll'>
                  {error && (
                    <div className='projects-drawer-error'>{error}</div>
                  )}
                  {!error &&
                    projects.length === 0 &&
                    !loading &&
                    hasFetched && (
                      <div className='projects-drawer-empty'>
                        No projects yet.
                      </div>
                    )}
                  {projects.map((thread) => {
                    const hasUnread = unreadThreadIds.has(thread.id);
                    return (
                      <button
                        key={thread.id}
                        type='button'
                        className='projects-drawer-item'
                        onClick={() => handleSelect(thread.id)}
                      >
                        <div className='projects-drawer-item-img'>
                          <img
                            src={
                              getProjectTagIconUrl(thread) ||
                              '/splicons/project-tags/other.png'
                            }
                            alt=''
                          />
                        </div>
                        <div className='projects-drawer-item-content'>
                          <div className='projects-drawer-item-title'>
                            {thread.description || 'Untitled Project'}
                          </div>
                          {hasUnread && (
                            <div className='projects-drawer-item-unread'>
                              <span className='unread-dot' />
                              <span className='unread-label'>Unread</span>
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                  {loading && (
                    <div className='projects-drawer-loading'>Loading…</div>
                  )}
                </div>
              </motion.aside>
            </React.Fragment>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
};

export default ProjectsDrawer;
